import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, ViewEncapsulation} from "@angular/core";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {AbstractWizardStepComponent, Wizard} from "../../../../../../../common/interfaces/wizard.interface";
import {Base} from "../../../../../../../common/interfaces/base.interfaces";
import {HelpersService} from "../../../../../../../common/services/helpers.service";
import {Api, ApiService} from "../../../../../../../common/services/api.service";
import {ToastService} from "../../../../../../../common/services/toast.service";
import {ModalService} from "../../../../../../services/modal.service";
import {SpinnerService} from "../../../../../../../common/services/spinner.service";
import {takeUntil} from "rxjs/operators";
import {Warehouse} from "../../../../../../../common/interfaces/warehouse.interface";

@Component({
    selector: "section-partner-procedures-wizard-update-item",
    templateUrl: "update-item.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class PartnerProceduresWizardUpdateItemComponent extends AbstractWizardStepComponent implements OnDestroy {

    private state: Base.IState;

    public part_masters: any[] = [];

    public hubs: { group: string, hubs: Warehouse.IHub[] }[] = [];

    public form: FormGroup = new FormGroup({
        inventory_id: new FormControl(null, [Validators.required]),
        part_master_id: new FormControl(null, [Validators.required]),
        inventory_conversion_id: new FormControl(null, [Validators.required]),
        new_part_master_id: new FormControl(null, [Validators.required]),
        quantity: new FormControl(null, [Validators.required]),
        remark: new FormControl(null, [Validators.required]),
    });

    public inventories: { inventory: Warehouse.IInventory, sum: number } [];

    public maxQty: number = 0;

    public constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        public helperService: HelpersService,
        private apiService: ApiService,
        private toastService: ToastService,
        private modalService: ModalService,
        private spinnerService: SpinnerService
    ) {
        super(changeDetectorRef);
    }


    public async getInventories(): Promise<any> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.apiService.request(Api.EMethod.Post,
            ["inventory", "available", "by_order_item"],
            {
                part_master_id: this.form.value.part_master_id,
                inventory_conversion_id: this.form.value.inventory_conversion_id,
                with_analogs: false,
                relations: [
                    "WarehouseLocation"
                ]
            });
        this.spinnerService.hide();
        if (data) {
            const inventories: { [key: string]: { inventory: Warehouse.IInventory, sum: number } } = {};
            for (const inventory of data) {
                const key: string = inventory.item + ":" + (inventory.configurations || "")
                    + ":" + (inventory.rev || "") + ":" + (inventory.batch || "")
                    + ":" + (inventory.warehouse_location_id || null);

                if (inventories[key]) {
                    inventories[key].sum += inventory.quantity;
                } else {
                    inventories[key] = {
                        inventory,
                        sum: inventory.quantity
                    };
                }
            }

            this.inventories = Object.values(inventories);

            this.changeDetectorRef.markForCheck();
        }
    }

    public selectInventory(inventory: { inventory: Warehouse.IInventory, sum: number }): void {
        this.maxQty = inventory.sum;
        this.form.get("inventory_id").setValue(inventory.inventory.id);
        this.form.get("quantity").setValidators([Validators.required, Validators.max(this.maxQty)]);
        this.form.get("quantity").updateValueAndValidity();
        this.changeDetectorRef.markForCheck();
    }

    /**
     * Submit form
     */
    public async submit(): Promise<any> {
        this.spinnerService.show();
        const {type, message}: Api.IResponse = await this.apiService.request(Api.EMethod.Put,
            ["inventory", "item-change"], this.form.value);
        if (type as string === "success") {
            this.toastService.show(message, "success");
            this.form.reset();
            this.changeDetectorRef.markForCheck();
        }
        this.spinnerService.hide();
    }

    /**
     * Initialize step
     * @param data
     * @returns {Promise<any>}
     */
    public async init(data: Wizard.IData): Promise<any> {
        this.state = data.state;

        this.result.emit({
            action: "result",
            value: true
        });

        this.form.get("inventory_conversion_id").valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((val: number): void => {
                if (val) {
                    setTimeout((): void => {
                        this.getInventories();
                    }, 10);
                }
            });

        this.form.get("part_master_id").valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((val: number): void => {
                this.inventories = null;
                this.changeDetectorRef.markForCheck();
                this.form.get("inventory_conversion_id").reset();
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
}
